import { t } from '@/lib/i18n';
import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { GridContent } from '@ant-design/pro-components';
import { Grid, Menu, Space, message } from 'antd';
import { useAppModal } from '@/components/app-modal';
import { AccountOverview } from '@/features/account/overview';
import { AccountSecurity } from '@/features/account/security';
import { AccountSessions } from '@/features/account/session/device-sessions';
import { AccountOwnSessions } from '@/features/account/session/own-sessions';
import { useSession, useSignOut } from '@/features/auth/session/session-api';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { describeError } from '@/lib/http/http-api';
import { sessionExpiresAt } from '@/lib/auth-client';

export const Route = createFileRoute('/_authenticated/profile')({
  component: ProfilePage,
});

type AccountTab = 'base' | 'security';

const tabLabels: Record<AccountTab, string> = {
  base: t('基本设置'),
  security: t('安全设置'),
};

const menuItems = (Object.keys(tabLabels) as AccountTab[]).map((key) => ({
  key,
  label: tabLabels[key],
}));

const isAccountTab = (value: string): value is AccountTab =>
  value === 'base' || value === 'security';

/**
 * 个人中心 — ant-design-pro's account/settings page, with this app's account in it.
 *
 * The reference does not use Tabs here: it is a GridContent holding a white panel, a 224px
 * antd Menu on the left, and the section name (20px) above the view on the right. The menu
 * turns horizontal and the panel stacks below screenMD, which is what the single breakpoint
 * below reproduces.
 *
 * There is no PageContainer header either — the reference puts the section title in the right
 * column and leaves signing out to the shell's avatar dropdown. The 登录会话 row on the
 * security pane is the second way to reach it.
 *
 * The session is read from the query rather than from the route context so that a change made
 * on the security pane — enabling two-step verification, say — is reflected immediately: the
 * context holds the snapshot beforeLoad took, which only moves on the next navigation.
 */
function ProfilePage() {
  useDocumentTitle(t('个人中心'));
  const { isAdmin, session: routeSession } = Route.useRouteContext();
  const session = useSession().data ?? routeSession;
  const signOut = useSignOut();
  const navigate = useNavigate();
  const modal = useAppModal();
  const screens = Grid.useBreakpoint();
  const [tab, setTab] = useState<AccountTab>('base');

  // screenMD, the width the reference switches at.
  const narrow = screens.md !== true;
  const email = session.user.email;

  const confirmSignOut = () => {
    modal.confirm({
      title: t('确认退出登录？'),
      content: t('退出后需重新登录才能继续使用。'),
      okText: t('退出登录'),
      cancelText: t('取消'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await signOut();
          await navigate({ to: '/login', search: { redirect: '/' } });
        } catch (error) {
          message.error(describeError(error));
        }
      },
    });
  };

  return (
    <GridContent>
      <div
        className={`flex w-full h-full py-4 bg-[var(--ant-color-bg-container)] ${narrow ? 'flex-col' : 'flex-row'}`}
      >
        <div
          className={`shrink-0 ${narrow ? 'w-full' : 'w-56 [border-inline-end:1px_solid_var(--ant-color-split)]'}`}
        >
          <Menu
            mode={narrow ? 'horizontal' : 'inline'}
            selectedKeys={[tab]}
            items={menuItems}
            onClick={({ key }) => {
              if (isAccountTab(key)) setTab(key);
            }}
            // The panel owns the divider; the inline menu's own edge would double it.
            className={`[border-inline-end:none] ${narrow ? 'font-bold' : ''}`}
          />
        </div>

        <div className="flex-1 min-w-0 p-(--app-panel-padding)">
          <div className="mb-3 text-[var(--ant-color-text-heading)] font-medium text-[20px] leading-[28px]">
            {tabLabels[tab]}
          </div>

          {tab === 'base' ? (
            <AccountOverview session={session} isAdmin={isAdmin} />
          ) : null}

          {tab === 'security' ? (
            <Space orientation="vertical" size={16} className="flex">
              <AccountSecurity
                email={email}
                username={session.user.username}
                emailVerified={session.user.emailVerified === true}
                twoFactorEnabled={session.user.twoFactorEnabled === true}
                mustChangePassword={session.user.mustChangePassword === true}
                sessionExpiresAt={sessionExpiresAt(session)}
                onSignOut={confirmSignOut}
              />
              <AccountOwnSessions
                userId={session.user.id}
                currentSessionId={session.session.id}
              />
              {/* The account switcher manages accounts saved in this browser, not all devices. */}
              <AccountSessions
                currentSessionId={session.session.id}
                onSwitched={() => window.location.reload()}
              />
            </Space>
          ) : null}
        </div>
      </div>
    </GridContent>
  );
}
